import { useState, useEffect } from 'react'

interface VisualViewportState {
  height: number | undefined
  keyboardVisible: boolean
}

/**
 * Tracks the visual viewport height and detects the iOS virtual keyboard.
 *
 * When the keyboard is open (`keyboardVisible === true`), `height` equals the
 * visual viewport height (the area above the keyboard). The consumer should
 * re-anchor the sheet from `bottom:0` → `top:0; bottom:auto` so that iOS
 * Safari's scroll-to-input behaviour has nothing left to fight.
 *
 * When the keyboard is closed, `height` is capped at 90 % of the layout
 * viewport (matching `max-h-[90dvh]` used elsewhere).
 *
 * @param enabled – only attach listeners when the sheet is open
 */
export function useVisualViewportHeight(enabled = true): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>({
    height: undefined,
    keyboardVisible: false,
  })

  useEffect(() => {
    if (!enabled) {
      setState({ height: undefined, keyboardVisible: false })
      return
    }

    const vv = window.visualViewport

    const update = () => {
      const vvHeight = vv ? vv.height : window.innerHeight
      // window.innerHeight is the layout viewport – it does NOT shrink when
      // the iOS keyboard opens, so it's a stable reference.
      const layoutHeight = window.innerHeight
      const isKeyboard = vv ? vvHeight < layoutHeight * 0.75 : false

      setState({
        height: isKeyboard ? vvHeight : Math.min(vvHeight, layoutHeight * 0.9),
        keyboardVisible: isKeyboard,
      })

      if (isKeyboard) {
        // Undo any residual scroll iOS applied before our handler fired.
        window.scrollTo(0, 0)
      }
    }

    update()

    if (vv) {
      vv.addEventListener('resize', update)
      vv.addEventListener('scroll', update)
    }
    window.addEventListener('resize', update)

    return () => {
      if (vv) {
        vv.removeEventListener('resize', update)
        vv.removeEventListener('scroll', update)
      }
      window.removeEventListener('resize', update)
    }
  }, [enabled])

  return state
}
